import { useEffect, useMemo, useRef, useState } from 'react'
import { fbt, useLocaleContext } from 'fbtee'
import { Tabs, TabsContent } from '../../../components/ui/tabs'
import { InterestCard } from '../../../components/InterestCard'
import { DayTabsList } from '../../../components/DayTabsList'
import { EmptyState } from '../../../components/EmptyState'
import { useSwipe } from '../../../hooks/useSwipe'
import { useHistoryState } from '../../../hooks/useHistoryState'
import { formatDayShort } from '../../../lib/formatDate'
import { cn } from '../../../lib/utils'
import { track } from '../../../lib/analytics'
import { useTransitionNavigate } from '../../../lib/viewTransition'
import type { SetFeed } from '../../../hooks/useSetPosts'
import type { LineupEntry, Stage, Artist, UserProfile } from '../../../types'
import { LineupTieredGrid } from './LineupTieredGrid'
import { LineupSearch } from './LineupSearch'
import { FollowedArtistsCta } from './FollowedArtistsCta'
import { actKey } from '@shared/lineupActs'
import { buildArtistContext, dedupeByAct, groupBy, matchLineupArtists } from './lineupSearchUtils'

function LineupDayModeSkeleton() {
  return (
    <div className="space-y-4">
      <DayTabsList loading />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <InterestCard key={`tier1-${i}`} loading className="aspect-square" />
        ))}
      </div>
      <div className="border-t border-border" />
      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: 9 }).map((_, i) => (
          <InterestCard key={`tier3-${i}`} loading />
        ))}
      </div>
    </div>
  )
}

export function LineupView({
  lineup,
  lineupLoading,
  lineupMode,
  stages,
  artists,
  interestedArtistIds,
  onToggleArtist,
  onMarkArtists,
  followHintIds,
  followCtaArtistIds,
  onAddFollowed,
  artistUserIds,
  userProfiles,
  currentUserId,
  defaultDay,
  selectedStage,
  onSelectedStageChange,
  feed,
  showSearch = true,
  tiered = true,
}: {
  lineup: LineupEntry[]
  lineupLoading: boolean
  lineupMode: 'stage' | 'day'
  stages: Stage[]
  artists: Record<string, Artist>
  interestedArtistIds: Set<string>
  onToggleArtist: (artistId: string) => void
  /** An act's heart: hearts every performer at once. */
  onMarkArtists: (artistIds: string[]) => void
  /** Followed app-wide but not hearted for this event — a rose-outlined heart. */
  followHintIds: ReadonlySet<string>
  /** What "Add the artists you follow" would heart; empty hides it. */
  followCtaArtistIds: string[]
  onAddFollowed: () => void
  artistUserIds: Record<string, string[]>
  userProfiles: Record<string, UserProfile>
  currentUserId?: string | null
  defaultDay?: string | null
  selectedStage?: string | null
  onSelectedStageChange?: (stage: string) => void
  feed?: SetFeed
  /** Off for short lineups (a party's handful of artists), where it's noise. */
  showSearch?: boolean
  /**
   * Off for a party, where a dozen artists split into headliner/support grids
   * reads as three lonely rows. Tier still drives the order, just not the
   * layout.
   */
  tiered?: boolean
}) {
  const stageMap: Record<string, Stage> = {}
  stages.forEach(s => {
    stageMap[s.id] = s
  })
  const { locale } = useLocaleContext()
  const navigate = useTransitionNavigate()
  // Day tabs are browsing state within this view: switching tabs opens on
  // `defaultDay` (today) again, but swiping back from an artist page keeps the pick.
  const [pickedDay, setPickedDay] = useHistoryState<string | null>('lineupDay', null)


  const [query, setQuery] = useState('')
  const needle = query.trim().toLowerCase()
  const searching = needle.length > 0
  const matched = useMemo(
    () => (searching ? matchLineupArtists(lineup, artists, needle) : []),
    [searching, lineup, artists, needle],
  )
  const contextMap = useMemo(
    () => buildArtistContext(lineup, stages, locale),
    [lineup, stages, locale],
  )
  const contextFor = (entry: LineupEntry) => contextMap.get(actKey(entry))


  // Stage grouping computed at hook level so the swipe callback's refs can be
  // synced in an effect instead of written during render (react-hooks/refs).
  const byStage = useMemo(() => groupBy(lineup, e => e.stageId ?? '__tba__'), [lineup])
  const sortedStageIds = useMemo(() => {
    const known = new Set(stages.map(s => s.id))
    return [
      ...stages.map(s => s.id).filter(id => byStage[id]),
      ...Object.keys(byStage).filter(id => id !== '__tba__' && !known.has(id)),
      ...(byStage['__tba__'] ? ['__tba__'] : []),
    ]
  }, [stages, byStage])
  const activeStage =
    selectedStage && sortedStageIds.includes(selectedStage) ? selectedStage : sortedStageIds[0]

  // Refs kept current so the banner swipe callback always reads the latest
  // active stage + stage list.
  const activeStageRef = useRef('')
  const stageIdsRef = useRef<string[]>([])
  useEffect(() => {
    activeStageRef.current = activeStage
    stageIdsRef.current = sortedStageIds
  }, [activeStage, sortedStageIds])
  const bannerSwipeRef = useSwipe(dir => {
    const ids = stageIdsRef.current
    const idx = ids.indexOf(activeStageRef.current)
    if (dir === 'left' && idx < ids.length - 1) onSelectedStageChange?.(ids[idx + 1])
    else if (dir === 'right' && idx > 0) onSelectedStageChange?.(ids[idx - 1])
  })

  if (lineupLoading && lineup.length === 0) {
    return <LineupDayModeSkeleton />
  }

  if (lineup.length === 0) {
    return (
      <EmptyState>
        <fbt desc="Empty state message when the event lineup has not yet been announced">
          Lineup not yet announced.
        </fbt>
      </EmptyState>
    )
  }

  function handleArtistNameClick(artistId: string) {
    track('artist_detail_opened', { artist_id: artistId, entry_point: 'lineup' })
    navigate(`/artists/${artistId}`)
  }

  const orderEntries = (list: LineupEntry[]) =>
    tiered ? list : [...list].sort((a, b) => (a.tier ?? 99) - (b.tier ?? 99))

  const gridProps = {
    flat: !tiered,
    artists,
    interestedArtistIds,
    onToggleArtist,
    onMarkArtists,
    followHintIds,
    onArtistNameClick: handleArtistNameClick,
    artistUserIds,
    userProfiles,
    currentUserId,
    feed,
  }

  const tabbedContent = (() => {
    if (lineupMode === 'day') {
      const byDay = groupBy(lineup, e => e.day ?? '__tba__')
      const sortedDays = Object.keys(byDay).sort((a, b) =>
        a === '__tba__' ? 1 : b === '__tba__' ? -1 : a.localeCompare(b),
      )
      const activeDay =
        [pickedDay, defaultDay].find(d => d && sortedDays.includes(d)) ?? sortedDays[0]
      if (sortedDays.length <= 1) {
        return <LineupTieredGrid entries={orderEntries(dedupeByAct(byDay[sortedDays[0]] ?? lineup))} {...gridProps} />
      }
      return (
        <Tabs value={activeDay} onValueChange={setPickedDay}>
          <DayTabsList
            days={sortedDays}
            bleed
            formatLabel={day =>
              day === '__tba__'
                ? fbt('Day TBA', 'Label for artists whose day assignment has not been announced')
                : formatDayShort(day, locale)
            }
          />
          {sortedDays.map(day => (
            <TabsContent key={day} value={day} className="mt-4">
              <LineupTieredGrid entries={orderEntries(dedupeByAct(byDay[day]))} {...gridProps} />
            </TabsContent>
          ))}
        </Tabs>
      )
    }

    if (sortedStageIds.length > 1) {
      const hasAnyImage = sortedStageIds.some(id => stageMap[id]?.imageUrl)
      return (
        <Tabs value={activeStage} onValueChange={onSelectedStageChange}>
          {hasAnyImage ? (
            <div
              ref={bannerSwipeRef}
              className="relative aspect-[16/8] sm:aspect-[16/6.5] rounded-lg overflow-hidden bg-muted"
            >
              {sortedStageIds.map(stageId => {
                const stage = stageMap[stageId]
                if (!stage?.imageUrl) return null
                return (
                  <img
                    key={stageId}
                    src={stage.imageUrl}
                    alt={stage.name}
                    className={cn(
                      'absolute inset-0 w-full h-full object-cover object-bottom transition-all duration-700 ease-out',
                      activeStage === stageId ? 'opacity-100 scale-100' : 'opacity-0 scale-105',
                    )}
                    loading="lazy"
                  />
                )
              })}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
              <div className="absolute inset-x-0 bottom-0 [mask-image:linear-gradient(to_bottom,transparent,black_30%)]">
                <DayTabsList
                  days={sortedStageIds}
                  formatLabel={stageId =>
                    stageId === '__tba__'
                      ? fbt(
                          'Stage TBA',
                          'Label for artists whose stage assignment has not been announced',
                        )
                      : (stageMap[stageId]?.name ?? stageId)
                  }
                  className="bg-background/70 backdrop-blur-md rounded-none"
                />
              </div>
            </div>
          ) : (
            <DayTabsList
              days={sortedStageIds}
              formatLabel={stageId =>
                stageId === '__tba__'
                  ? fbt(
                      'Stage TBA',
                      'Label for artists whose stage assignment has not been announced',
                    )
                  : (stageMap[stageId]?.name ?? stageId)
              }
            />
          )}
          {sortedStageIds.map(stageId => (
            <TabsContent key={stageId} value={stageId} className="mt-4">
              <LineupTieredGrid entries={orderEntries(dedupeByAct(byStage[stageId]))} {...gridProps} />
            </TabsContent>
          ))}
        </Tabs>
      )
    }

    const onlyStage = stageMap[sortedStageIds[0]]
    return (
      <div className="space-y-4">
        {onlyStage?.imageUrl && (
          <img
            src={onlyStage.imageUrl}
            alt={onlyStage.name}
            className="w-full aspect-[16/8] sm:aspect-[16/6.5] object-cover object-bottom rounded-lg"
            loading="lazy"
          />
        )}
        <LineupTieredGrid entries={orderEntries(dedupeByAct(byStage[sortedStageIds[0]]))} {...gridProps} />
      </div>
    )
  })()

  return (
    <div className="space-y-4">
      {showSearch && <LineupSearch value={query} onChange={setQuery} />}
      {!searching && followCtaArtistIds.length > 0 && (
        <FollowedArtistsCta count={followCtaArtistIds.length} onAdd={onAddFollowed} />
      )}
      {searching ? (
        matched.length === 0 ? (
          <EmptyState>
            <fbt desc="Empty state when no artists match the lineup search query">
              No artists match your search.
            </fbt>
          </EmptyState>
        ) : (
          <LineupTieredGrid entries={matched} subtitleFor={contextFor} {...gridProps} flat />
        )
      ) : (
        tabbedContent
      )}
    </div>
  )
}
